import { useCallback, useEffect, useMemo, useState } from 'react';
import { isEmpty } from '../../../utils/customMethods';
import { trackWarrantySelection } from '../../Cart/Cart.Utils';

/**
 * Custom hook that encapsulates all business logic for Warranty component
 * @param {Object} props - Component props
 * @returns {Object} - Processed data and handlers
 */
export const useWarranty = ({ warrantyOptions, setWarrantyOption, cart, appCtx, digitalData }) => {
  const { warrantyOptionCurrent } = cart || {};

  const firstWarranty = warrantyOptions?.fieldOptions?.[0];

  const [showModal, setShowModal] = useState(false);
  const [highlightedWarranty, setHighlightedWarranty] = useState(firstWarranty);

  const labels = useMemo(() => {
    const textDictionary = appCtx?.siteConfig?.textDictionary ?? {};
    return {
      EXTENDED_WARRANTY_LABEL: textDictionary.EXTENDED_WARRANTY_LABEL ?? 'Extiende la garantía del producto',
      MORE_WARRANTY_OPTIONS: textDictionary.MORE_WARRANTY_OPTIONS ?? 'Conocer más opciones',
      WARRANTY_LABEL_TEMPLATE: textDictionary.WARRANTY_LABEL_TEMPLATE ?? '{warrantyName}',
    };
  }, [appCtx?.siteConfig?.textDictionary]);

  const isImprovedWarrantyUiEnabled = useMemo(() => {
    return appCtx?.siteConfig?.toggles?.isImprovedWarrantyUiEnabled ?? true;
  }, [appCtx?.siteConfig?.toggles?.isImprovedWarrantyUiEnabled]);

  const tooltipContent = useMemo(() => {
    return !isEmpty(warrantyOptions?.warrantyTooltip) ? warrantyOptions.warrantyTooltip : undefined;
  }, [warrantyOptions?.warrantyTooltip]);

  const hasWarrantyOptions = useMemo(() => {
    return Boolean(warrantyOptions?.fieldOptions?.length);
  }, [warrantyOptions?.fieldOptions?.length]);

  const currentVariant = useMemo(() => {
    return appCtx?.product?.currentVariant ?? '';
  }, [appCtx?.product?.currentVariant]);

  useEffect(() => {
    if (warrantyOptionCurrent) {
      setHighlightedWarranty(warrantyOptionCurrent);
    }
  }, [warrantyOptionCurrent]);

  useEffect(() => {
    if (hasWarrantyOptions && digitalData?.pdp?.setExtendedWarrantyOptsQty) {
      digitalData.pdp.setExtendedWarrantyOptsQty(warrantyOptions.fieldOptions.length);
    }
  }, [hasWarrantyOptions, warrantyOptions?.fieldOptions?.length, digitalData]);

  const toggleModalVisibility = useCallback(() => {
    setShowModal((prevState) => !prevState);
  }, []);

  const handleWarrantyChange = useCallback(
    (option) => {
      setHighlightedWarranty(option);
      setWarrantyOption(option);

      if (cart?.setWarrantyOptionCurrent) {
        cart.setWarrantyOptionCurrent(option);
      }

      if (option?.selected) {
        trackWarrantySelection('PDP', currentVariant, option.name, option.value || option.variantId || option.id);
      }
    },
    [setWarrantyOption, cart, currentVariant]
  );

  const selectHandler = useCallback(
    (option) => {
      const updatedOption = { ...option, selected: !option.selected };

      if (updatedOption.selected) {
        setWarrantyOption(updatedOption);
        trackWarrantySelection(
          'PDP',
          currentVariant,
          updatedOption.name,
          updatedOption.value || updatedOption.variantId || updatedOption.id
        );
        return;
      }

      setWarrantyOption();
      setHighlightedWarranty(updatedOption);
    },
    [setWarrantyOption, currentVariant]
  );

  const highlightedWarrantyData = useMemo(() => {
    if (!highlightedWarranty) {
      return {};
    }

    const { offeringId, selected, textPrice, prices, discountBadge, name } = highlightedWarranty;

    return {
      offeringId,
      selected,
      textPrice,
      prices,
      discountBadge,
      name,
    };
  }, [highlightedWarranty]);

  const computedValues = useMemo(
    () => ({
      shouldRender: hasWarrantyOptions,
      isReady: Boolean(warrantyOptions && highlightedWarranty),
      hasTooltip: Boolean(tooltipContent),
      modalProps: {
        appCtx,
        options: warrantyOptions?.fieldOptions ?? [],
        showModal,
        setShowModal,
        handleWarrantyChange,
        warrantyOptionCurrent,
      },
    }),
    [
      hasWarrantyOptions,
      warrantyOptions,
      highlightedWarranty,
      tooltipContent,
      appCtx,
      showModal,
      handleWarrantyChange,
      warrantyOptionCurrent,
    ]
  );

  return {
    // State values
    showModal,
    setShowModal,
    highlightedWarranty,

    // Processed data with spread
    ...highlightedWarrantyData,

    // Configuration
    labels,
    isImprovedWarrantyUiEnabled,
    tooltipContent,

    // Event handlers
    toggleModalVisibility,
    handleWarrantyChange,
    selectHandler,

    // Computed values with spread
    ...computedValues,
  };
};
